import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Card, Table, Modal, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash, faPlus, faArrowUp, faArrowDown } from '@fortawesome/free-solid-svg-icons';
// Import from Firebase services
import { getDoctors, addDoctor, updateDoctor, deleteDoctor } from '../../services/firebase/doctorService';
import { compressImageFile } from '../../services/firebase/imageUtils';

const AdminDoctors = () => {
  // State variables
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [currentDoctor, setCurrentDoctor] = useState(null);
  const [validated, setValidated] = useState(false);
  const [imagePreview, setImagePreview] = useState('');

  // Fetch team members on component mount
  useEffect(() => {
    fetchDoctors();
  }, []);

  // Fetch team members from Firebase
  const fetchDoctors = async () => {
    try {
      setLoading(true);
      const doctorsList = await getDoctors();
      setDoctors(doctorsList);
      setError('');
    } catch (err) {
      setError('Failed to load team members. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Handle modal close
  const handleCloseModal = () => {
    setShowModal(false);
    setCurrentDoctor(null);
    setValidated(false);
    setImagePreview('');
  };

  // Handle modal show
  const handleShowModal = (doctor = null) => {
    setCurrentDoctor(doctor);
    if (doctor && doctor.image) {
      setImagePreview(doctor.image);
    } else {
      setImagePreview('');
    }
    setShowModal(true);
  };

  // Handle form submission
  const handleSubmit = async (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    
    if (form.checkValidity() === false) {
      event.stopPropagation();
      setValidated(true);
      return;
    }

    try {
      const formData = {
        name: form.doctorName.value,
        specialty: form.doctorSpecialty.value,
        education: form.doctorEducation.value,
        experience: form.doctorExperience.value,
        image: imagePreview || '/images/placeholder-doctor.svg',
        social: {
          linkedin: form.doctorLinkedin.value || '',
          twitter: form.doctorTwitter.value || ''
        }
      };

      if (currentDoctor && currentDoctor.id) {
        // Update existing doctor
        await updateDoctor(currentDoctor.id, formData);
      } else {
        // Add new doctor
        await addDoctor(formData);
      }

      // Refresh the doctors list
      fetchDoctors();
      handleCloseModal();
    } catch (err) {
      console.error('Error saving doctor:', err);
      setError(`Failed to save doctor: ${err.message || 'Please try again.'}`);
    }
  };

  // Handle image change — compress before storing so the Firestore document
  // stays under the 1 MiB limit (uncompressed photos would exceed it and the
  // save would silently fail).
  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      setError('Image file is too large. Please select an image under 10MB.');
      return;
    }

    try {
      setError('');
      const compressedDataUrl = await compressImageFile(file);
      setImagePreview(compressedDataUrl);
    } catch (err) {
      console.error('Error processing image:', err);
      setError(err.message || 'Error processing the image. Please try another image.');
    }
  };

  // Handle team member deletion
  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this team member?')) {
      try {
        setError('');
        setLoading(true);
        console.log('Attempting to delete doctor with ID:', id);
        const result = await deleteDoctor(id);
        console.log('Delete result:', result);
        
        // Refresh the team members list
        await fetchDoctors();
        setError('');
      } catch (err) {
        console.error('Full error in AdminDoctors handleDelete:', err);
        setError(`Failed to delete team member: ${err.message || 'Unknown error'}. Please check if you have permission.`);
      } finally {
        setLoading(false);
      }
    }
  };

  // Handle moving team member up in order
  const handleMoveUp = async (index) => {
    if (index > 0) {
      const newDoctors = [...doctors];
      const currentDoctor = newDoctors[index];
      const previousDoctor = newDoctors[index - 1];
      
      // Swap the order values
      const tempOrder = currentDoctor.order || index;
      currentDoctor.order = previousDoctor.order || (index - 1);
      previousDoctor.order = tempOrder;
      
      // Swap positions in array
      [newDoctors[index], newDoctors[index - 1]] = [newDoctors[index - 1], newDoctors[index]];
      setDoctors(newDoctors);
      
      // Update in Firebase
      try {
        await updateDoctor(currentDoctor.id, { ...currentDoctor, order: currentDoctor.order });
        await updateDoctor(previousDoctor.id, { ...previousDoctor, order: previousDoctor.order });
      } catch (error) {
        console.error('Error updating order:', error);
        setError('Failed to update order. Please try again.');
      }
    }
  };

  // Handle moving team member down in order
  const handleMoveDown = async (index) => {
    if (index < doctors.length - 1) {
      const newDoctors = [...doctors];
      const currentDoctor = newDoctors[index];
      const nextDoctor = newDoctors[index + 1];
      
      // Swap the order values
      const tempOrder = currentDoctor.order || index;
      currentDoctor.order = nextDoctor.order || (index + 1);
      nextDoctor.order = tempOrder;
      
      // Swap positions in array
      [newDoctors[index], newDoctors[index + 1]] = [newDoctors[index + 1], newDoctors[index]];
      setDoctors(newDoctors);
      
      // Update in Firebase
      try {
        await updateDoctor(currentDoctor.id, { ...currentDoctor, order: currentDoctor.order });
        await updateDoctor(nextDoctor.id, { ...nextDoctor, order: nextDoctor.order });
      } catch (error) {
        console.error('Error updating order:', error);
        setError('Failed to update order. Please try again.');
      }
    }
  };

  return (
    <Container fluid className="p-4">
      <Row className="mb-4">
        <Col>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h4 className="mb-0">Manage Team Members</h4>
              <Button variant="primary" onClick={() => handleShowModal()}>
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                Add New Team Member
              </Button>
            </Card.Header>
            <Card.Body>
              {error && <Alert variant="danger">{error}</Alert>}
              
              {loading ? (
                <div className="text-center p-4">Loading team members...</div>
              ) : (
                <Table responsive striped hover>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Specialty</th>
                      <th>Experience</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {doctors.length === 0 ? (
                      <tr>
                        <td colSpan="4" className="text-center">No team members found. Add your first team member!</td>
                      </tr>
                    ) : (
                      doctors.map((doctor, index) => (
                        <tr key={doctor.id}>
                          <td>{doctor.name}</td>
                          <td>{doctor.specialty}</td>
                          <td>{doctor.experience}</td>
                          <td>
                            <div className="d-flex align-items-center gap-2">
                              {/* Ordering buttons */}
                              <div className="d-flex flex-column">
                                <Button 
                                  variant="outline-secondary" 
                                  size="sm" 
                                  className="mb-1"
                                  onClick={() => handleMoveUp(index)}
                                  disabled={index === 0}
                                  title="Move up"
                                  style={{ minWidth: '32px', padding: '4px' }}
                                >
                                  <FontAwesomeIcon icon={faArrowUp} />
                                </Button>
                                <Button 
                                  variant="outline-secondary" 
                                  size="sm"
                                  onClick={() => handleMoveDown(index)}
                                  disabled={index === doctors.length - 1}
                                  title="Move down"
                                  style={{ minWidth: '32px', padding: '4px' }}
                                >
                                  <FontAwesomeIcon icon={faArrowDown} />
                                </Button>
                              </div>
                              
                              {/* Edit and Delete buttons */}
                              <div className="d-flex gap-2">
                                <Button 
                                  variant="outline-primary" 
                                  size="sm" 
                                  onClick={() => handleShowModal(doctor)}
                                  title="Edit"
                                  style={{ minWidth: '32px' }}
                                >
                                  <FontAwesomeIcon icon={faEdit} />
                                </Button>
                                <Button 
                                  variant="outline-danger" 
                                  size="sm"
                                  onClick={() => handleDelete(doctor.id)}
                                  title="Delete"
                                  style={{ minWidth: '32px' }}
                                >
                                  <FontAwesomeIcon icon={faTrash} />
                                </Button>
                              </div>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Add/Edit Team Member Modal */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {currentDoctor && currentDoctor.id ? 'Edit Team Member' : 'Add New Team Member'}
          </Modal.Title>
        </Modal.Header>
        <Form noValidate validated={validated} onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3" controlId="doctorName">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter team member's name"
                required
                defaultValue={currentDoctor ? currentDoctor.name : ''}
              />
              <Form.Control.Feedback type="invalid">
                Please provide a name.
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3" controlId="doctorSpecialty">
              <Form.Label>Role/Specialty</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter role or specialty"
                required
                defaultValue={currentDoctor ? currentDoctor.specialty : ''}
              />
              <Form.Control.Feedback type="invalid">
                Please provide a role or specialty.
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3" controlId="doctorEducation">
              <Form.Label>Education</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter education details"
                defaultValue={currentDoctor ? currentDoctor.education : ''}
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="doctorExperience">
              <Form.Label>Experience</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter years of experience"
                required
                defaultValue={currentDoctor ? currentDoctor.experience : ''}
              />
              <Form.Control.Feedback type="invalid">
                Please provide experience details.
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3" controlId="doctorImage">
              <Form.Label>Team Member Image</Form.Label>
              <Form.Control
                type="file"
                accept="image/*"
                onChange={handleImageChange}
              />
              <Form.Text className="text-muted">
                Upload a professional photo of the team member.
              </Form.Text>
            </Form.Group>

            {imagePreview && (
              <Form.Group className="mb-3">
                <Form.Label>Image Preview</Form.Label>
                <div className="text-center">
                  <img
                    src={imagePreview}
                    alt="Team member preview"
                    className="img-thumbnail"
                    style={{ width: '200px', height: '200px', objectFit: 'contain' }}
                  />
                </div>
              </Form.Group>
            )}

            <Form.Group className="mb-3" controlId="doctorLinkedin">
              <Form.Label>LinkedIn Profile URL (Optional)</Form.Label>
              <Form.Control
                type="url"
                placeholder="https://linkedin.com/in/username"
                defaultValue={currentDoctor && currentDoctor.social && currentDoctor.social.linkedin ? currentDoctor.social.linkedin : ''}
              />
              <Form.Text className="text-muted">
                Leave blank if not applicable
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3" controlId="doctorTwitter">
              <Form.Label>Twitter Profile URL (Optional)</Form.Label>
              <Form.Control
                type="url"
                placeholder="https://twitter.com/username"
                defaultValue={currentDoctor && currentDoctor.social && currentDoctor.social.twitter ? currentDoctor.social.twitter : ''}
              />
              <Form.Text className="text-muted">
                Leave blank if not applicable
              </Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              Save
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
};

export default AdminDoctors;